'use client';

import { Box, Flex } from '@chakra-ui/react';
import { useEffect, useState, type PointerEventHandler, type ReactNode } from 'react';
import { useAppRail } from './AppRailContext';
import {
  APP_RAIL_COLLAPSED_WIDTH,
  APP_RAIL_EXPANDED_WIDTH,
  APP_RAIL_WIDTH_CSS_VARIABLE,
} from './app-rail-state';

const RAIL_MOTION = '200ms cubic-bezier(0.2, 0, 0, 1)';
const RAIL_REVEAL_OFFSET = `calc(${APP_RAIL_EXPANDED_WIDTH} - ${APP_RAIL_COLLAPSED_WIDTH})`;

/** A moving clip and opposing content transform reveal a fixed layout without scaling text. */
export function AppRailFrame({
  collapsed,
  floating,
  ready,
  onPointerEnter,
  onPointerLeave,
  onPointerCancel,
  children,
}: {
  collapsed: boolean;
  floating: boolean;
  ready: boolean;
  onPointerEnter: PointerEventHandler<HTMLDivElement>;
  onPointerLeave: PointerEventHandler<HTMLDivElement>;
  onPointerCancel: PointerEventHandler<HTMLDivElement>;
  children: ReactNode;
}) {
  const { holdRailHover } = useAppRail();
  const [focusWithin, setFocusWithin] = useState(false);
  useEffect(() => {
    if (focusWithin) return holdRailHover();
  }, [focusWithin, holdRailHover]);
  const panelTransform = collapsed
    ? `translateX(calc(-1 * ${RAIL_REVEAL_OFFSET}))`
    : 'translateX(0)';

  return (
    <Box
      data-section="appShellRail"
      data-collapsed={collapsed}
      display={{ base: 'none', lg: 'block' }}
      position={collapsed || floating ? 'absolute' : 'relative'}
      top="0"
      left="0"
      h="full"
      minH={0}
      w={floating ? APP_RAIL_EXPANDED_WIDTH : `var(${APP_RAIL_WIDTH_CSS_VARIABLE}, ${APP_RAIL_EXPANDED_WIDTH})`}
      flexShrink={0}
      zIndex={3}
      bg={collapsed ? 'transparent' : 'ui.surface.rail'}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerCancel={onPointerCancel}
    >
      <Box
        aria-hidden="true"
        position="absolute"
        insetBlock="0"
        left="0"
        w={APP_RAIL_EXPANDED_WIDTH}
        borderRadius="0 12px 12px 0"
        pointerEvents="none"
        boxShadow="var(--attune-shadow-floating, none)"
        opacity={floating && !collapsed ? 1 : 0}
        transform={panelTransform}
        transition={ready ? `transform ${RAIL_MOTION}, opacity ${RAIL_MOTION}` : 'none'}
        _motionReduce={{ transition: 'none' }}
      />
      <Box
        id="app-navigation-rail"
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setFocusWithin(false);
        }}
        inert={collapsed || !ready}
        pointerEvents={collapsed ? 'none' : 'auto'}
        position="absolute"
        insetBlock="0"
        left="0"
        w={APP_RAIL_EXPANDED_WIDTH}
        overflow="hidden"
        borderRadius={floating ? '0 12px 12px 0' : '0'}
        outline={floating ? '1px solid' : 'none'}
        outlineColor="ui.border.divider"
        outlineOffset="-1px"
        borderRightWidth={floating ? '0' : '1px'}
        borderColor="ui.border.divider"
        transform={panelTransform}
        transition={ready ? `transform ${RAIL_MOTION}` : 'none'}
        _motionReduce={{ transition: 'none' }}
      >
        <Flex
          direction="column"
          h="full"
          minH={0}
          w={APP_RAIL_EXPANDED_WIDTH}
          px="3"
          bg="ui.surface.rail"
          overflow="hidden"
          transform={collapsed ? `translateX(${RAIL_REVEAL_OFFSET})` : 'translateX(0)'}
          transition={ready ? `transform ${RAIL_MOTION}` : 'none'}
          visibility={ready ? 'visible' : 'hidden'}
          _motionReduce={{ transition: 'none' }}
          css={{
            '& [data-rail-label]': {
              opacity: collapsed ? 0 : 1,
              transition: ready ? 'opacity 120ms ease' : 'none',
            },
            '@media (prefers-reduced-motion: reduce)': {
              '& [data-rail-label]': { transition: 'none' },
            },
          }}
        >
          {children}
        </Flex>
      </Box>
    </Box>
  );
}
